// ATLAS · TARGETS — the sector's Endeavour → Goal → Aim from the ATLAS store (user_settings.oracle_spine via
// olmps/atlas-data.ts — the same state the ATLAS tool edits). The Aim's cadence is the activity's own stored recurrence.
import { effectiveAims } from '../../../../olmps/oracle-spine-data';
import { deityLabel, goalName } from '../../shared';
import type { OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel, openAction } from './util';

const BASE = { id: 'telos', toolId: 'atlas', orderFunction: 'orient', title: 'TARGETS', deityName: 'ATLAS', agentId: 'telos' } as const;

export function useTelosWidget(c: WidgetCtx): OrbWidgetModel {
  const node = c.sector && c.spine ? c.spine[c.sector] ?? null : null;
  const endeavour = node?.endeavour ?? null;
  const goal = goalName(c.sector, node);
  const deity = deityLabel(c.sector, node);
  const aims = node ? effectiveAims(node) : [];
  const aim = aims[0] ?? null;

  if (!endeavour && !goal && !aim) {
    return emptyModel(BASE, c, 'No destination set', { label: 'Edit targets', run: () => c.openTool('atlas'), tone: 'gold' });
  }

  const lines: string[] = [];
  if (c.sectorName) lines.push(c.sectorName);
  if (endeavour) lines.push(`Endeavour: ${endeavour}${deity ? ` · ${deity}` : ''}`);
  if (goal) lines.push(`Goal: ${goal}`);
  if (aim) lines.push(`Aim: ${aim.label}${aim.cadence ? ` · ${aim.cadence}` : ''}`);
  if (aims.length > 1) lines.push(`+${aims.length - 1} more Aim${aims.length > 2 ? 's' : ''}`);

  const noAim = !!endeavour && !aim;
  const noGoal = !!endeavour && !goal;
  const raw = aim ? `Aim: ${aim.label}` : goal ? `Goal: ${goal}` : `Endeavour: ${endeavour}`;
  return {
    ...BASE,
    status: noAim || noGoal ? 'attention' : 'quiet',
    summary: raw.length > 40 ? `${raw.slice(0, 39).trimEnd()}…` : raw,
    alert: noAim ? 'Endeavour has no Aim' : noGoal ? 'Endeavour has no Goal' : null,
    lines,
    primaryAction: { label: 'Edit targets', run: () => c.openTool('atlas'), tone: 'gold' },
    secondaryActions: [],
    signals: { active: !!aim, alerted: noAim, related: !!aim },
  };
}
